// src/api/axios.js
import axios from "axios";
import i18n from "../i18n";

// Base URL depuis Vite/CRA, sinon fallback relatif
const API_URL =
  (typeof import.meta !== "undefined" &&
    import.meta.env &&
    import.meta.env.VITE_API_BASE_URL) ||
  process.env.REACT_APP_API_URL ||
  "/api/v1";

// Callback (optionnel) déclenché sur 401 pour rediriger (ex: /login)
let onUnauthorized = null;
export function setUnauthorizedHandler(fn) {
  onUnauthorized = typeof fn === "function" ? fn : null;
}

export function getUserMessage(error, fallback = "Une erreur est survenue") {
  const r = error?.response;
  const s = r?.status;

  if (!r) return "Serveur injoignable. Vérifie ta connexion ou réessaie.";

  const rawDetail = r?.data?.detail;

  // 1) Cas structuré : { code, message, params }
  if (
    rawDetail &&
    typeof rawDetail === "object" &&
    !Array.isArray(rawDetail) &&
    rawDetail.code
  ) {
    const { code, message, params } = rawDetail;
    const key = `errors.${code}`;
    try {
      const translated = i18n?.t ? i18n.t(key, params || {}) : key;
      if (translated && translated !== key) return translated;
    } catch {}
    if (message) return message;
    return fallback;
  }

  // 2) Cas non structuré
  const detail =
    (typeof rawDetail === "string" && rawDetail) ||
    (Array.isArray(rawDetail) && rawDetail.map((e) => e.msg || e.type).join(", ")) ||
    r?.data?.message ||
    null;

  // 3) Mappings UX par status HTTP
  if (s === 400) return detail || "Requête invalide.";
  if (s === 401) return detail || "Session expirée. Merci de vous reconnecter.";
  if (s === 403) return detail || "Action non autorisée.";
  if (s === 404) return detail || "Ressource introuvable.";
  if (s === 409) {
    if ((r.config?.url || "").includes("/reservations")) {
      return detail || "Créneau non disponible (chevauchement). Choisis un autre horaire.";
    }
    return detail || "Conflit de ressource.";
  }
  if (s === 422) return detail || "Données invalides. Vérifie le formulaire.";
  if (s === 429) return "Trop de requêtes. Réessaie dans un instant.";
  if (s >= 500) return detail || "Erreur serveur. Réessaie plus tard.";

  return detail || fallback;
}

// Axios instance
const api = axios.create({
  baseURL: API_URL,
  timeout: 40000,
});

// Detect SSO callback route
function isInSsoCallback() {
  try {
    return (window?.location?.pathname || "").startsWith("/sso/callback");
  } catch {
    return false;
  }
}

// Request interceptor: Bearer + i18n + timezone
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token") || localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;

  const lang = (i18n?.language || "fr").split("-")[0];
  config.headers["Accept-Language"] = lang;

  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz) config.headers["X-Timezone"] = tz;
  } catch {}

  return config;
});

// Response interceptor: messages + 401 handling (SSO-safe)
api.interceptors.response.use(
  (res) => res,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url;
    const method = error?.config?.method;

    if (error?.code === "ECONNABORTED") {
      console.warn(
        "[API TIMEOUT]",
        method?.toUpperCase?.() || "GET",
        url,
        "après",
        error?.config?.timeout,
        "ms"
      );
    }

    error.userMessage = getUserMessage(error);

    if (status === 401) {
      // ✅ 2 garde-fous:
      // 1) ne pas casser le flow /sso/callback
      // 2) permettre un override par requête: { skipUnauthorizedRedirect: true }
      const inSso = isInSsoCallback();
      const skipByConfig = Boolean(error?.config?.skipUnauthorizedRedirect);

      if (!inSso && !skipByConfig) {
        try {
          localStorage.removeItem("access_token");
          localStorage.removeItem("token");
        } catch {}

        if (onUnauthorized) {
          try {
            onUnauthorized();
          } catch {}
        }
      }
    }

    return Promise.reject(error);
  }
);

export default api;
